import { produce } from 'immer';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import VoucherBanner from '../../../../../../common/assets/images/voucher_banner.png';
import {
  formatDate,
  showErrorToast,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import { Barcode } from '../../../../../../common/widgets/Barcode';
import Button from '../../../../../../common/widgets/Button';
import Dialog from '../../../../../../common/widgets/Dialog';
import Icons from '../../../../../../common/widgets/Icons';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { QRCode } from '../../../../../../common/widgets/QRCode';
import { BarcodeTypes, VoucherCodeTypes } from '../../../../enums';
import { rewriteImageUrl } from '../../../../helpers';
import { VoucherDetailsModel } from '../../../../models/Vouchers';
import { resources } from '../../../../resources/resources';
import { voucherService } from '../../../../services/vouchers';
import { ToggleUsageTarget, VouchersTabs } from '..';

interface DownloadVoucherDialogProps {
  productId: number;
  orderId: number;
  codeType: VoucherCodeTypes;
  codeIntId: number | null;
  codeStringId: string | null;
  activeTab: VouchersTabs;
  onToggleUsage: (voucher: ToggleUsageTarget) => Promise<boolean>;
  closeDialog: () => void;
}

interface DownloadVoucherDialogState {
  voucherDetails: VoucherDetailsModel | undefined;
  isDownloadButtonLoading?: boolean;
  isPrintButtonLoading?: boolean;
  isEmailButtonLoading?: boolean;
  isMarkUsageButtonLoading?: boolean;
  showErrorPanel?: boolean;
}

export const DownloadVoucherDialog = (props: DownloadVoucherDialogProps) => {
  const {
    productId,
    orderId,
    codeType,
    codeIntId,
    codeStringId,
    activeTab,
    onToggleUsage,
    closeDialog,
  } = props;
  const [state, setState] = useState<DownloadVoucherDialogState>({
    voucherDetails: undefined,
  });
  const { t: translate } = useTranslation();

  const loadVoucherDetails = async (abortController?: AbortController) => {
    const resp = await voucherService.getVoucherDetails(
      productId,
      orderId,
      codeType,
      codeIntId,
      codeStringId,
      {
        signal: abortController?.signal,
      },
    );
    if (!resp.hasErrors) {
      setState({
        voucherDetails: {
          ...resp.data,
          codeExpirationDate: formatDate(resp.data.codeExpirationDate),
          date: formatDate(resp.data.date),
        },
      });
    } else {
      if (!abortController?.signal.aborted) {
        setState({ showErrorPanel: true, voucherDetails: undefined });
      }
    }
  };

  useMount(loadVoucherDetails);

  const getBarcode = (code: string, barcodeType: BarcodeTypes) => {
    if (barcodeType === BarcodeTypes.QR) return <QRCode code={code} />;
    return (
      <Barcode
        code={code}
        options={{
          format: barcodeType === BarcodeTypes.Code39 ? 'CODE39' : 'CODE128',
        }}
      />
    );
  };

  const downloadVoucher = async () => {
    setState(
      produce(d => {
        d.isDownloadButtonLoading = true;
      }),
    );
    const resp = await voucherService.downloadVoucher(
      productId,
      orderId,
      codeType,
      codeIntId,
      codeStringId,
      state.voucherDetails!.name,
    );
    setState(
      produce(d => {
        d.isDownloadButtonLoading = false;
      }),
    );
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.cannotGenerateFile));
    }
  };

  const printVoucher = async () => {
    setState(
      produce(d => {
        d.isPrintButtonLoading = true;
      }),
    );
    const resp = await voucherService.printVoucher(
      productId,
      orderId,
      codeType,
      codeIntId,
      codeStringId,
    );
    setState(
      produce(d => {
        d.isPrintButtonLoading = false;
      }),
    );
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.cannotGenerateFile));
    }
  };

  const emailVoucher = async () => {
    setState(
      produce(d => {
        d.isEmailButtonLoading = true;
      }),
    );
    const resp = await voucherService.emailVoucher(
      productId,
      orderId,
      codeType,
      codeIntId,
      codeStringId,
    );
    setState(
      produce(d => {
        d.isEmailButtonLoading = false;
      }),
    );
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.cannotGenerateFile));
    }
  };

  const toggleUsage = async () => {
    if (!state.voucherDetails) return;

    setState(
      produce(d => {
        d.isMarkUsageButtonLoading = true;
      }),
    );

    const success = await onToggleUsage({
      orderProductId: state.voucherDetails.orderProductId,
      codeType,
      codeIntId,
      codeStringId,
      isVoucherUsed: state.voucherDetails.isVoucherUsed,
    });

    if (success) {
      closeDialog();
      return;
    }

    setState(
      produce(d => {
        d.isMarkUsageButtonLoading = false;
      }),
    );
  };

  const markUsageLabel =
    activeTab === VouchersTabs.Active
      ? translate(resources.vouchers.markAsUsed)
      : translate(resources.vouchers.markAsUnused);

  return (
    <Dialog
      closeOnClickOutside
      showCloseIcon
      closeDialog={closeDialog}
      className="dialog-xl dialog-voucher"
    >
      <div className="dialog-voucher-image">
        <img src={VoucherBanner} />
      </div>
      <Loader
        loading={!state.voucherDetails}
        loader={<LoadingSpinner />}
        showErrorPanel={state.showErrorPanel}
      >
        {!!state.voucherDetails && (
          <>
            <div className="voucher-logo">
              <img src={rewriteImageUrl(state.voucherDetails.image)} />
            </div>
            <div className="dialog-content">
              <div>
                <h2>{state.voucherDetails.name}</h2>
                <ul className="voucher-details">
                  <li>
                    {translate(resources.vouchers.code)}:{' '}
                    {state.voucherDetails.code}
                  </li>
                  {state.voucherDetails.value && (
                    <li>
                      {translate(resources.vouchers.value)}:{' '}
                      {state.voucherDetails.value}
                    </li>
                  )}
                  {state.voucherDetails.codeExpirationDate && (
                    <li>
                      {translate(resources.vouchers.expirationDate)}:{' '}
                      {state.voucherDetails.codeExpirationDate}
                    </li>
                  )}
                  {state.voucherDetails.pin && (
                    <li>
                      {translate(resources.vouchers.pin)}:{' '}
                      {state.voucherDetails.pin}
                    </li>
                  )}
                  <li>
                    {translate(resources.vouchers.date)}:{' '}
                    {state.voucherDetails.date}
                  </li>
                </ul>
                <div
                  className="voucher-info text-reset"
                  dangerouslySetInnerHTML={{
                    __html: state.voucherDetails.description ?? '',
                  }}
                />
              </div>
            </div>

            {state.voucherDetails.barcodeType !== BarcodeTypes.Alphanumeric && (
              <div className="voucher-barcode">
                {getBarcode(
                  state.voucherDetails.code,
                  state.voucherDetails.barcodeType,
                )}
              </div>
            )}

            <div className="dialog-actions horizontal">
              <Button
                className="btn btn-solid dialog-action-download"
                loading={state.isDownloadButtonLoading}
                disabled={
                  state.isPrintButtonLoading ||
                  state.isDownloadButtonLoading ||
                  state.isEmailButtonLoading ||
                  state.isMarkUsageButtonLoading
                }
                onClick={downloadVoucher}
                icon={<Icons.DownloadFile />}
              >
                {translate(resources.download.pdf)}
              </Button>
              <Button
                className="btn btn-outline dialog-action-print"
                loading={state.isPrintButtonLoading}
                disabled={
                  state.isPrintButtonLoading ||
                  state.isDownloadButtonLoading ||
                  state.isEmailButtonLoading ||
                  state.isMarkUsageButtonLoading
                }
                onClick={printVoucher}
                icon={<Icons.Print />}
              >
                {translate(resources.print)}
              </Button>
              <Button
                className="btn btn-outline dialog-action-email"
                loading={state.isEmailButtonLoading}
                disabled={
                  state.isPrintButtonLoading ||
                  state.isDownloadButtonLoading ||
                  state.isEmailButtonLoading ||
                  state.isMarkUsageButtonLoading
                }
                onClick={emailVoucher}
                icon={<Icons.Send />}
              >
                {translate(resources.sendByEmail)}
              </Button>
              <Button
                className="btn btn-outline dialog-action-mark-used"
                loading={state.isMarkUsageButtonLoading}
                disabled={
                  state.isPrintButtonLoading ||
                  state.isDownloadButtonLoading ||
                  state.isEmailButtonLoading ||
                  state.isMarkUsageButtonLoading
                }
                onClick={toggleUsage}
                icon={<Icons.ArrowButton />}
              >
                {markUsageLabel}
              </Button>
            </div>
          </>
        )}
      </Loader>
    </Dialog>
  );
};
